/**
 * URL contract for the Requests workspace, inside the client profile:
 *
 *   /clients/:fnId?tab=outgoing_intakes                     the Requests list
 *   /clients/:fnId?tab=outgoing_intakes&stage=<stage>       the list filtered to one stage
 *   /clients/:fnId?tab=outgoing_intakes&open=intake:<id>    the builder, or the tracking page once sent
 *   /clients/:fnId?tab=outgoing_intakes&open=submission:<intakeId>/<submissionId>  the review page
 *   /clients/:fnId?tab=outgoing_intakes&open=preview:<id>   the intake with its preview open
 *   /clients/:fnId?tab=outgoing_intakes&open=template:<templateId>  redirects to Firm Knowledge
 *   /clients/:fnId?tab=outgoing_intakes&new=blank | templates:<a,b> | saved:<id> | ai | docs:<visa>
 *
 * `open` is the page: it stays in the URL so refresh, back and shared links
 * land where they were. `new` is consumed once (the workspace starts the
 * action, then strips it) so a refresh does not repeat it. The legacy `oi_*`
 * keys the old Requests panel used are migrated in
 * `migrateLegacyIntakeParams` so every existing deep link still lands.
 */

import { STAGES, type Stage } from "./model";
import {
  parseArtifactKey,
  type NewRequestAction,
  type RequestArtifact
} from "./types";

export const OPEN_SEARCH_PARAM = "open";
export const NEW_SEARCH_PARAM = "new";
export const STAGE_SEARCH_PARAM = "stage";
export const REQUESTS_TAB = "outgoing_intakes";

/** The legacy Requests panel's query keys. */
export const LEGACY_INTAKE_QUERY_KEYS = [
  "oi_mode",
  "oi_id",
  "oi_template",
  "oi_title",
  "oi_docs_only",
  "oi_visa_workflow",
  "oi_workflow_filter",
  "oi_return_tab"
] as const;

export type OpenRequest = Pick<RequestArtifact, "kind" | "id">;

export const parseOpenParam = (search: string): OpenRequest | null => {
  const raw = (new URLSearchParams(search).get(OPEN_SEARCH_PARAM) ?? "").trim();
  if (!raw) return null;
  return parseArtifactKey(raw);
};

/** The stage the list is filtered to; null for all of them (or junk). */
export const parseStageParam = (search: string): Stage | null => {
  const raw = (
    new URLSearchParams(search).get(STAGE_SEARCH_PARAM) ?? ""
  ).trim();
  return STAGES.find((s) => s.id === raw)?.id ?? null;
};

export const parseNewParam = (search: string): NewRequestAction | null => {
  const raw = (new URLSearchParams(search).get(NEW_SEARCH_PARAM) ?? "").trim();
  if (!raw) return null;
  if (raw === "blank") return { kind: "blank" };
  if (raw === "ai") return { kind: "ai" };
  if (raw.startsWith("templates:")) {
    const ids = raw
      .slice("templates:".length)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return ids.length ? { kind: "templates", templateIds: ids } : null;
  }
  if (raw.startsWith("docs:")) {
    const visa = raw.slice("docs:".length).trim();
    return visa ? { kind: "docs", visa } : null;
  }
  if (raw.startsWith("saved:")) {
    const templateId = raw.slice("saved:".length).trim();
    return templateId ? { kind: "saved", templateId } : null;
  }
  return null;
};

export const serializeNewAction = (action: NewRequestAction): string => {
  switch (action.kind) {
    case "ai":
      return "ai";
    case "templates":
      return `templates:${action.templateIds.join(",")}`;
    case "docs":
      return `docs:${action.visa}`;
    case "saved":
      return `saved:${action.templateId}`;
    case "blank":
    default:
      return "blank";
  }
};

/** Search string for the Requests tab with an artifact to open. */
export const buildOpenSearch = (
  currentSearch: string,
  open?: OpenRequest | null,
  action?: NewRequestAction | null
): string => {
  const params = new URLSearchParams(currentSearch);
  params.set("tab", REQUESTS_TAB);
  for (const key of LEGACY_INTAKE_QUERY_KEYS) params.delete(key);
  params.delete(OPEN_SEARCH_PARAM);
  params.delete(NEW_SEARCH_PARAM);
  params.delete(STAGE_SEARCH_PARAM);
  if (open) params.set(OPEN_SEARCH_PARAM, `${open.kind}:${open.id}`);
  if (action) params.set(NEW_SEARCH_PARAM, serializeNewAction(action));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

/** Search string for the Requests list: the tab with no page open, filtered to `stage` when given. */
export const buildListSearch = (
  currentSearch: string,
  stage?: Stage | null
): string => {
  const base = buildOpenSearch(currentSearch, null, null);
  if (!stage) return base;
  const params = new URLSearchParams(base);
  params.set(STAGE_SEARCH_PARAM, stage);
  return `?${params.toString()}`;
};

/** Path into a client's Requests workspace with an intake open. */
export const intakePath = (clientId: string, intakeId: string): string =>
  `/clients/${encodeURIComponent(
    clientId
  )}?tab=${REQUESTS_TAB}&${OPEN_SEARCH_PARAM}=intake:${encodeURIComponent(
    intakeId
  )}`;

export const submissionPath = (
  clientId: string,
  intakeId: string,
  submissionId: string
): string =>
  `/clients/${encodeURIComponent(
    clientId
  )}?tab=${REQUESTS_TAB}&${OPEN_SEARCH_PARAM}=submission:${encodeURIComponent(
    intakeId
  )}/${encodeURIComponent(submissionId)}`;

/**
 * Rewrite the old Requests panel's `oi_*` parameters into the IDE's `open` /
 * `new` contract. Returns the same search when nothing legacy is present.
 *
 *   oi_mode=view|edit|review|view_review & oi_id=X  → open=intake:X
 *     (review modes open the intake; the IDE opens its latest submission
 *      itself when the intake is awaiting review)
 *   oi_mode=create & oi_docs_only=1 & oi_visa_workflow=V → new=docs:V
 *   oi_mode=create & oi_template=T                        → new=templates:T
 *   oi_mode=create                                        → new=ai
 */
export const migrateLegacyIntakeParams = (search: string): string => {
  const params = new URLSearchParams(search);
  const hasLegacy = LEGACY_INTAKE_QUERY_KEYS.some((k) => params.has(k));
  if (!hasLegacy) return search;
  const mode = (params.get("oi_mode") ?? "").trim();
  const id = (params.get("oi_id") ?? "").trim();
  const template = (params.get("oi_template") ?? "").trim();
  const docsOnly = (params.get("oi_docs_only") ?? "").trim() === "1";
  const visa = (params.get("oi_visa_workflow") ?? "").trim();
  for (const key of LEGACY_INTAKE_QUERY_KEYS) params.delete(key);
  params.set("tab", REQUESTS_TAB);
  if (id && mode !== "create") {
    params.set(OPEN_SEARCH_PARAM, `intake:${id}`);
  } else if (mode === "create") {
    if (docsOnly && visa) params.set(NEW_SEARCH_PARAM, `docs:${visa}`);
    else if (template) params.set(NEW_SEARCH_PARAM, `templates:${template}`);
    else params.set(NEW_SEARCH_PARAM, "ai");
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

export const hasLegacyIntakeParams = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return LEGACY_INTAKE_QUERY_KEYS.some((k) => params.has(k));
};
